import { useState } from 'react'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { MoreVertical, EyeOff, Eye, Pencil } from '@/components/icons'
import { Card } from './ui/card'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from './ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
} from './ui/alert-dialog'
import { Button } from './ui/button'

dayjs.extend(relativeTime)

interface RecipientInfo {
  id: string
  firstName?: string | null
  lastName?: string | null
  avatarColor?: string | null
}

interface AdminAnnouncementCardProps {
  title: string
  message: string
  createdAt: number
  viewedPercentage: number
  recipients: RecipientInfo[]
  published: boolean
  onEdit?: () => void
  onUnpublishConfirm: () => Promise<void>
  onPublish?: () => Promise<void>
  onClick?: () => void
}

function getInitials(member: RecipientInfo) {
  const first = member.firstName?.charAt(0) ?? ''
  const last = member.lastName?.charAt(0) ?? ''
  return (first + last).toUpperCase() || '?'
}

export function AdminAnnouncementCard({
  title,
  message,
  createdAt,
  viewedPercentage,
  recipients,
  published,
  onEdit,
  onUnpublishConfirm,
  onPublish,
  onClick,
}: AdminAnnouncementCardProps) {
  const [isUnpublishDialogOpen, setIsUnpublishDialogOpen] = useState(false)
  const [isUnpublishing, setIsUnpublishing] = useState(false)
  const [isPublishing, setIsPublishing] = useState(false)

  const handleUnpublish = async () => {
    setIsUnpublishing(true)
    try {
      await onUnpublishConfirm()
      setIsUnpublishDialogOpen(false)
    } finally {
      setIsUnpublishing(false)
    }
  }

  const handlePublish = async () => {
    if (!onPublish) return
    setIsPublishing(true)
    try {
      await onPublish()
    } finally {
      setIsPublishing(false)
    }
  }

  return (
    <>
      <Card
        interactive={!!onClick}
        onClick={onClick}
        className="flex flex-col gap-3 px-3 py-[7.5px]"
      >
        {/* Header: title + relative time + menu */}
        <div className="flex items-start gap-3">
          <div className="flex-1 min-w-0 flex flex-col gap-2">
            <div className="flex flex-col gap-1 min-w-0">
              <p className="font-semibold text-base text-content-primary-bold leading-6 tracking-[-0.5px] truncate">
                {title}
              </p>
              <p className="text-sm text-content-primary-subtle leading-5 truncate">
                {message}
              </p>
            </div>
            <span className="text-xs text-content-primary-subtle font-semibold leading-4">
              {dayjs(createdAt).fromNow()}
            </span>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                onClick={(e) => e.stopPropagation()}
                className="shrink-0 p-1 -mr-1 rounded-md text-content-primary-subtle hover:text-content-primary-bold hover:bg-surface-L2 transition-colors"
                disabled={isPublishing}
              >
                <MoreVertical className="size-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
              {published ? (
                <>
                  {onEdit && (
                    <>
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation()
                          onEdit()
                        }}
                      >
                        <Pencil className="size-4" />
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                    </>
                  )}
                  <DropdownMenuItem
                    variant="destructive"
                    onClick={(e) => {
                      e.stopPropagation()
                      setIsUnpublishDialogOpen(true)
                    }}
                  >
                    <EyeOff className="size-4" />
                    Unpublish
                  </DropdownMenuItem>
                </>
              ) : (
                onPublish && (
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation()
                      handlePublish()
                    }}
                  >
                    <Eye className="size-4" />
                    Publish
                  </DropdownMenuItem>
                )
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Divider */}
        <div className="h-px bg-border" />

        {/* Footer: viewed pill + avatars + status badge */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2.5 min-w-0">
            <span className="shrink-0 bg-surface-L2 text-content-primary-bold text-xs font-semibold px-2 py-[3px] rounded-lg leading-4">
              {viewedPercentage}% Viewed
            </span>
            {recipients.length > 0 && (
              <div className="flex items-center">
                {recipients.slice(0, 6).map((member, i) => (
                  <div
                    key={member.id}
                    className="size-6 rounded-full flex items-center justify-center text-xs leading-4 font-semibold text-content-primary-inverse overflow-hidden"
                    style={{
                      backgroundColor: member.avatarColor ?? 'var(--muted)',
                      marginLeft: i === 0 ? 0 : '-4px',
                      zIndex: recipients.length - i,
                    }}
                  >
                    {getInitials(member)}
                  </div>
                ))}
              </div>
            )}
          </div>
          {published ? (
            <span className="shrink-0 bg-background-positive-subtle text-background-positive-bold text-xs font-semibold px-2 py-[3px] rounded-full leading-4">
              Published
            </span>
          ) : (
            <span className="shrink-0 bg-surface-L2 text-content-primary-subtle text-xs font-semibold px-2 py-[3px] rounded-full leading-4">
              Unpublished
            </span>
          )}
        </div>
      </Card>

      {/* Unpublish Confirmation Dialog */}
      <AlertDialog open={isUnpublishDialogOpen} onOpenChange={setIsUnpublishDialogOpen}>
        <AlertDialogContent className="p-8">
          <AlertDialogDescription className="text-center text-sm text-content-primary-subtle leading-5">
            Unpublish this announcement? Crew members will no longer see it.
          </AlertDialogDescription>
          <div className="flex flex-col gap-2 mt-2">
            <Button
              variant="destructive"
              size="mini"
              className="w-full"
              onClick={handleUnpublish}
              disabled={isUnpublishing}
            >
              <EyeOff className="size-4" />
              Unpublish
            </Button>
            <Button
              variant="secondary"
              size="mini"
              className="w-full"
              onClick={() => setIsUnpublishDialogOpen(false)}
              disabled={isUnpublishing}
            >
              Cancel
            </Button>
          </div>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
